import React, { FC, useEffect } from 'react';
import { format } from 'date-fns';

import { colors } from '@nerdwallet/base-styles/variables';
import { NDS_DATE_FORMAT } from '@constants/dates';

const FLYOUT_HEIGHT = 68;
const FLYOUT_WIDTH = 120;
const FLYOUT_POS_OFFSET = 10;
const FLYOUT_LINE_HEIGHT = 16;
const FLYOUT_PADDING = 2;

const flyoutTextBaseStyles = {
  x: '50%',
  dominantBaseline: 'middle',
  textAnchor: 'middle',
  fontSize: 13,
  fill: colors['blue-light'],
};
interface Datum {
  x: Date; // x-axis value
  y: number; // y-axis value
}
interface DashboardChartFlyoutProps {
  x: number; // svg x-coordinate
  y: number; // svg y-coordinate
  datum: Datum;
  active: boolean;
  yValueMedian: number;
  formatValue?: (val: number) => string;
  formatSubtext?: (val: number) => string;
  width: number;
  onBecomeActive?: (val: {
    formattedDate: string;
    formattedValue: any;
    formattedSubtext: any;
  }) => void;
}

const DashboardChartFlyout: FC<DashboardChartFlyoutProps> = ({
  x,
  y,
  datum: { x: xValue, y: yValue },
  active,
  width,
  yValueMedian,
  formatValue = (val) => val,
  formatSubtext = (val) => val,
  onBecomeActive = () => {},
}) => {
  const formattedDate = format(xValue, NDS_DATE_FORMAT);
  const formattedValue = formatValue(yValue);
  const formattedSubtext = formatSubtext(yValue);
  useEffect(
    () => {
      if (active) {
        onBecomeActive({ formattedDate, formattedValue, formattedSubtext });
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration
    [active, formattedDate, formattedValue, formattedSubtext],
  );

  if (!active) return null;

  let flyoutYPos;
  if (yValue > yValueMedian) {
    flyoutYPos = y + FLYOUT_POS_OFFSET;
  } else {
    flyoutYPos = y - FLYOUT_HEIGHT - FLYOUT_POS_OFFSET;
  }

  let flyoutXPos = x - FLYOUT_WIDTH / 2;
  const lowerXPosLimit = FLYOUT_WIDTH / 2 + FLYOUT_POS_OFFSET;
  const upperXPosLimit = width - FLYOUT_WIDTH - FLYOUT_POS_OFFSET;

  if (flyoutXPos < lowerXPosLimit) {
    flyoutXPos = lowerXPosLimit;
  } else if (flyoutXPos > upperXPosLimit) {
    flyoutXPos = upperXPosLimit;
  }

  return (
    <svg
      x={flyoutXPos}
      y={flyoutYPos}
      height={FLYOUT_HEIGHT}
      width={FLYOUT_WIDTH}
    >
      <rect
        x={0}
        y={0}
        width={FLYOUT_WIDTH}
        height={FLYOUT_HEIGHT}
        fill={colors.white}
        rx={3}
        ry={3}
      />
      <text {...flyoutTextBaseStyles} y={FLYOUT_PADDING + FLYOUT_LINE_HEIGHT}>
        {formattedValue}
      </text>
      <text
        {...flyoutTextBaseStyles}
        y={FLYOUT_PADDING + FLYOUT_LINE_HEIGHT * 2}
      >
        ({formattedSubtext})
      </text>
      <text
        {...flyoutTextBaseStyles}
        y={FLYOUT_PADDING + FLYOUT_LINE_HEIGHT * 3}
        fill={colors['neutral-darker']}
      >
        {formattedDate}
      </text>
    </svg>
  );
};

export default DashboardChartFlyout;
